import {
  ActionFunction,
  json,
  LoaderFunction,
  redirect,
  useActionData,
  useLoaderData,
  useSearchParams,
} from "remix";
import { BreadcrumbLink } from "~/components/Breadcrumbs";
import { Button } from "~/components/Button";
import { FormInput } from "~/components/FormInput";
import { db } from "~/utils/db.server";
import {
  createUserSession,
  getUser,
  login,
  register,
} from "~/utils/session.server";

function validateUsername(username: unknown) {
  if (typeof username !== "string" || username.length < 3) {
    return `Usernames must be at least 3 characters long`;
  }
}

function validatePassword(password: unknown) {
  if (typeof password !== "string" || password.length < 6) {
    return `Passwords must be at least 6 characters long`;
  }
}

export const handle = {
  breadcrumb: () => <BreadcrumbLink to="/login">Login</BreadcrumbLink>,
};

type ActionData = {
  formError?: string;
  fieldErrors?: {
    username: string | undefined;
    password: string | undefined;
  };
  fields?: {
    loginType: string;
    username: string;
    password: string;
  };
};

type LoaderData = {
  hasRegistrationToken: boolean;
};

const badRequest = (data: ActionData) => json(data, { status: 400 });

export const loader: LoaderFunction = async ({ request }) => {
  const url = new URL(request.url);

  const user = await getUser(request);

  if (user) {
    throw redirect("/");
  }

  const hasRegistrationToken = url.searchParams.has("registrationToken");

  return { hasRegistrationToken } as LoaderData;
};

export const action: ActionFunction = async ({ request }) => {
  const url = new URL(request.url);
  const form = await request.formData();
  const loginType = form.get("loginType");
  const username = form.get("username");
  const password = form.get("password");
  const redirectTo = form.get("redirectTo") || "/";

  if (
    typeof loginType !== "string" ||
    typeof username !== "string" ||
    typeof password !== "string" ||
    typeof redirectTo !== "string"
  ) {
    return badRequest({
      formError: `Form not submitted correctly.`,
    });
  }

  const fields = { loginType, username, password };
  const fieldErrors = {
    username: validateUsername(username),
    password: validatePassword(password),
  };
  if (Object.values(fieldErrors).some(Boolean))
    return badRequest({ fieldErrors, fields });

  switch (loginType) {
    case "login": {
      const user = await login({ username, password });
      if (!user) {
        return badRequest({
          fields,
          formError: `Username/Password combination is incorrect`,
        });
      }
      return createUserSession(user.id, redirectTo);
    }
    case "register": {
      try {
        const registrationToken = url.searchParams.get("registrationToken");

        if (typeof registrationToken !== "string") {
          throw new Error("Registration token not available");
        }

        const token = await db.registrationToken.findFirst({
          where: { token: registrationToken },
        });

        if (!token) {
          throw new Error("Registration token not found");
        }

        const userExists = await db.user.findFirst({
          where: { username },
        });

        if (!!userExists) {
          throw new Error(`User with username ${username} already exists`);
        }

        const user = await register({ username, password });

        if (!user) {
          throw new Error(`Something went wrong trying to create a new user.`);
        }

        await db.registrationToken.delete({ where: { id: token.id } });

        return createUserSession(user.id, redirectTo);
      } catch (error) {
        return badRequest({
          fields,
          formError:
            error instanceof Error ? error?.message : "Something went wrong",
        });
      }
    }
    default: {
      return badRequest({
        fields,
        formError: `Login type invalid`,
      });
    }
  }
};

export default function Login() {
  const { hasRegistrationToken } = useLoaderData<LoaderData>();
  const actionData = useActionData<ActionData>();
  const [searchParams] = useSearchParams();
  return (
    <div className="flex">
      <div className="w-full md:w-1/3 mx-auto my-5 p-8 md:shadow-lg rounded-lg">
        <h1>Login</h1>
        <form
          method="post"
          aria-describedby={
            actionData?.formError ? "form-error-message" : undefined
          }
        >
          <input
            type="hidden"
            name="redirectTo"
            value={searchParams.get("redirectTo") ?? undefined}
          />
          <fieldset>
            <legend className="sr-only">Login or Register?</legend>
            <label>
              <input
                type="radio"
                name="loginType"
                value="login"
                defaultChecked={
                  (!hasRegistrationToken && !actionData?.fields?.loginType) ||
                  actionData?.fields?.loginType === "login"
                }
              />{" "}
              Login
            </label>

            <label>
              <input
                type="radio"
                name="loginType"
                value="register"
                disabled={!hasRegistrationToken}
                defaultChecked={
                  hasRegistrationToken ||
                  actionData?.fields?.loginType === "register"
                }
              />{" "}
              Register
            </label>
          </fieldset>
          <div>
            <FormInput
              label="Username"
              id="username-input"
              name="username"
              defaultValue={actionData?.fields?.username}
              fieldError={actionData?.fieldErrors?.username}
            />
          </div>
          <div>
            <FormInput
              label="Password"
              id="password-input"
              name="password"
              defaultValue={actionData?.fields?.password}
              inputType="password"
              fieldError={actionData?.fieldErrors?.password}
            />
          </div>
          <div id="form-error-message">
            {actionData?.formError ? (
              <p className="form-validation-error" role="alert">
                {actionData?.formError}
              </p>
            ) : null}
          </div>
          <Button type="submit" className="mt-3 w-full flex justify-center">
            Submit
          </Button>
        </form>
      </div>
    </div>
  );
}
